import React, { useEffect, useState } from 'react';
import { Calendar, Link2, Unlink, RefreshCw, Loader2 } from 'lucide-react';
import {
  calendarApiError,
  disconnectGoogleCalendar,
  fetchGoogleCalendarStatus,
  getGoogleCalendarAuthUrl,
  syncGoogleCalendarEvents,
} from '../services/googleCalendar.service.ts';
import type { GoogleCalendarConnection } from '../types/calendar.types.ts';

type GoogleCalendarConnectProps = {
  onSync: () => void;
};

const GoogleCalendarConnect: React.FC<GoogleCalendarConnectProps> = ({ onSync }) => {
  const [connection, setConnection] = useState<GoogleCalendarConnection>({
    connected: false,
    syncEnabled: false,
  });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const status = await fetchGoogleCalendarStatus();
      if (!cancelled) {
        setConnection(status);
        setLoadingStatus(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    setMessage('');
    try {
      const authUrl = await getGoogleCalendarAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      setMessage(calendarApiError(error));
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectGoogleCalendar();
      setConnection({ connected: false, syncEnabled: false });
      setMessage('');
      onSync();
    } catch {
      setMessage('Bağlantı kesilemedi.');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setMessage('');
    try {
      await syncGoogleCalendarEvents();
      setMessage('Senkron tamamlandı.');
      onSync();
    } catch {
      setMessage('Senkron başarısız. Tekrar bağlanmayı deneyin.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-5 h-5 text-blue-400" />
        <h3 className="text-white font-semibold text-sm">Google Takvim</h3>
      </div>

      {loadingStatus ? (
        <div className="flex items-center gap-2 text-gray-400 text-xs py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Durum kontrol ediliyor...
        </div>
      ) : connection.connected ? (
        <div className="space-y-3">
          <p className="text-gray-400 text-xs">
            Bağlı: <span className="text-white">{connection.email}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleSync()}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs disabled:opacity-50"
            >
              {syncing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Senkronize et
            </button>
            <button
              type="button"
              onClick={() => void handleDisconnect()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs"
            >
              <Unlink className="w-3.5 h-3.5" />
              Bağlantıyı kes
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-gray-400 text-xs leading-relaxed">
            Google Takvim etkinliklerinizi TBKMeet takviminde görüntüleyin. Bağlıyken oluşturduğunuz
            planlı toplantılar Google Takviminize de otomatik eklenir.
          </p>
          <button
            type="button"
            onClick={() => void handleConnect()}
            disabled={connecting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-900 hover:bg-gray-100 rounded-lg text-sm font-semibold transition disabled:opacity-50"
          >
            {connecting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4" />
            )}
            Google ile bağlan
          </button>
        </div>
      )}

      {message && <p className="text-gray-500 text-[11px] mt-3">{message}</p>}

      <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap gap-3 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-purple-500" />
          TBKMeet online
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          Yüz yüze
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          Google
        </span>
      </div>
    </div>
  );
};

export default GoogleCalendarConnect;
